import styled from "@emotion/styled";
import { GrSelect, palette } from "@jnpmedi/grebob";
import { elementHeight } from "@newComponents/common/Viewer/EConsentDocsPdfViewerPageMarkerZone";
import { defaultMarkerColor, getColorBySignerIndex } from "@newComponents/common/Viewer/types";
import { MvnMessage } from "@newLibs/utils";
import { useDocumentMarkerContext } from "@newHooks/context/document/documentMarker.context";
import { fadeInBorderMarker } from "@assets/styles/animations";
import { EConsentMarkerProps } from "./EConsentMarker";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs";
import { DocumentSignerAttrType } from "@maven-rest/docs/DocumentSign.dto";

export function DropdownMarker(props: EConsentMarkerProps) {
  const { markers, setMarkers } = useDocumentMarkerContext();

  const getMarkerType = (attrType: string) => {
    switch (attrType) {
      case DocumentSignerAttrType.AgentDropdownItemMark:
        return {
          index: DocumentSignerAttrType.AgentDropdownItemIndex,
          missing: DocumentSignerAttrType.AgentDropdownItemMissing,
          value: DocumentSignerAttrType.AgentDropdownItemValue,
          options: DocumentSignerAttrType.AgentDropdownItemOptions,
        };
      default:
        return {
          index: DocumentSignerAttrType.DropdownItemIndex,
          missing: DocumentSignerAttrType.DropdownItemMissing,
          value: DocumentSignerAttrType.DropdownItemValue,
          options: DocumentSignerAttrType.DropdownItemOptions,
        };
    }
  };
  const markerTypes = getMarkerType(props.marker.attr);

  const dropdownItemIndex = markers.find((e) => e.attr === markerTypes.index && e.attrUuid === props.marker.groupUuid);
  const dropdownItemMissing = markers.find((e) => e.attr === markerTypes.missing && dropdownItemIndex?.attrUuid === e.groupUuid);
  const dropdownItemValue = markers.find((e) => e.attr === markerTypes.value && dropdownItemIndex?.attrUuid === e.groupUuid);
  const dropdownItemOptions = markers.find((e) => e.attr === markerTypes.options && dropdownItemIndex?.attrUuid === e.groupUuid);

  const handleDropdownChange = (newValue: string) => {
    const updatedMarkers = markers
      .filter(
        (x) => !(x.attr === markerTypes.value && x.attrUuid === dropdownItemValue?.attrUuid && x.groupUuid === dropdownItemValue?.groupUuid)
      )
      .concat([
        {
          id: dropdownItemValue?.id,
          signerUuid: dropdownItemValue?.signerUuid!,
          target: dropdownItemValue?.target!,
          signerIndex: dropdownItemValue?.signerIndex!,
          attr: markerTypes.value,
          attrUuid: dropdownItemValue?.attrUuid!,
          groupUuid: dropdownItemValue?.groupUuid!,
          value: newValue,
          page: dropdownItemValue?.page!,
          x: dropdownItemValue?.x!,
          y: dropdownItemValue?.y!,
        },
      ]);
    setMarkers(updatedMarkers);
  };

  return (
    <StyledSelectWrapper {...props} isSelected={!!dropdownItemValue?.value} isHidden={props.isHidden} onClick={props.onClick}>
      {props.isModeSigning ? (
        <GrSelect
          style={{ height: "20px", padding: "0px 2px 0px 0px" }}
          variant="text"
          placeholder={dropdownItemMissing?.value || MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_DROPDOWN_MARKER_PLACEHOLDER)}
          size="small"
          options={
            dropdownItemOptions?.value?.split(";").map((el) => ({
              value: el,
              label: el,
            })) || []
          }
          onChange={(e) => {
            handleDropdownChange(e.target.value);
          }}
          value={dropdownItemValue?.value || ""}
        />
      ) : (
        props.children
      )}
    </StyledSelectWrapper>
  );
}

const StyledSelectWrapper = styled("div")<EConsentMarkerProps & { isSelected: boolean; isHidden: boolean }>((props) => ({
  display: props.isHidden ? "none" : "block",
  userSelect: "none",
  boxSizing: "border-box",
  position: "absolute",
  top: props.top,
  left: props.left,
  transform: "translateY(-50%)",
  borderRadius: "2px",
  border: `2px solid ${
    props.isModeSigning ? palette[defaultMarkerColor.additional.border] : palette[getColorBySignerIndex(props.marker.signerIndex).border]
  }`,
  height: elementHeight,

  outline: "none",

  backgroundColor: props.isModeSigning
    ? defaultMarkerColor.additional.background
    : getColorBySignerIndex(props.marker.signerIndex).background,

  color: "#878d96",
  fontSize: "10px",
  fontWeight: 500,
  lineHeight: "135%",
  whiteSpace: "nowrap",
  textIndent: 0,

  cursor: "pointer",

  "&:hover": {
    backgroundColor: props.isModeSigning
      ? defaultMarkerColor.additional.backgroundHover
      : getColorBySignerIndex(props.marker.signerIndex).backgroundHover,
    //NOTE: marker hover 시, UnlinkButton이 보임
    "& > span": {
      opacity: 1,
    },
  },

  "&":
    !props.isSelected && props.isModeSigning
      ? {
          animation: `${fadeInBorderMarker} 1.6s ease-in-out infinite`,
        }
      : "",
}));
